import { Form, FormControl, FormGroup } from 'react-bootstrap';
import RankingSelect from '../../Form/RankingSelect/RankingSelect';
import IRankingsPair from '../../../interfaces/IRankingsPair';
import React, { useRef } from 'react';
import { IEvaluationRequirement } from '../../../interfaces/IApplicationRequirements';
import { FeedbackLevel } from '../../../interfaces/FeedbackLevel';
import { IPartData } from '../../../interfaces/IApplicationReviewForm';

interface Props {
    isAlgorithm: boolean;
    readOnly: boolean;
    req: IEvaluationRequirement;
    inputValue: IRankingsPair | string;
    inputComplete: boolean;
    onChange: (change: IPartData<IRankingsPair | string>) => void;
    onFeedback: (level: FeedbackLevel, msg: string) => void;
    minLength: number;
    maxLength?: number;
}

const EvaluationInput: React.FC<Props> = ({
    isAlgorithm,
    req,
    readOnly,
    inputValue,
    inputComplete,
    onChange,
    onFeedback,
    minLength,
    maxLength,
}) => {
    const [showValidation, setShowValidation] = React.useState(false);
    const formControlRef = useRef<any>();

    const inputValueString: string = !isAlgorithm && !!inputValue ? (inputValue as string) : '';

    const handleChangeComplete = (newComplete: boolean) => {
        setShowValidation(true);
        if (newComplete) {
            if (!isAlgorithm) {
                const result = formControlRef.current && formControlRef.current.checkValidity();
                if (!result) {
                    onFeedback('danger', 'Cannot mark as complete whilst form has errors');
                    return;
                }
            } else {
                const expectedValues = req.elements?.length || 1;
                if (
                    !inputValue ||
                    Object.keys(inputValue as IRankingsPair).length !== expectedValues
                ) {
                    onFeedback(
                        'danger',
                        'Cannot mark as complete until all elements have been assigned a value'
                    );
                    return;
                }
            }
        }
        onChange({
            value: inputValue,
            complete: newComplete,
        });
    };

    const handleValueChange = (newValue: string) => {
        onChange({
            value: newValue,
            complete: inputComplete,
        });
    };

    const handleRankingChange = (_name: string, value: IRankingsPair) => {
        onChange({
            value: value,
            complete: inputComplete,
        });
    };

    const valid =
        (!isAlgorithm &&
            inputValue !== undefined &&
            inputValueString.length >= minLength &&
            inputValueString.length <= (maxLength ?? Number.MAX_SAFE_INTEGER)) ||
        isAlgorithm;

    const invalid =
        (!isAlgorithm && inputValue !== undefined && inputValueString.length < minLength) ||
        inputValueString.length > (maxLength ?? Number.MAX_SAFE_INTEGER);

    return (
        <>
            {isAlgorithm && (
                <RankingSelect
                    handleChange={handleRankingChange}
                    handleFeedback={onFeedback}
                    readOnly={readOnly || inputComplete}
                    req={req}
                    value={inputValue as IRankingsPair}
                />
            )}
            {!isAlgorithm && (
                <FormGroup>
                    <Form.Label className={'text-black w-100'}>
                        <p className={'fw-bold'}>{req.subtitle}</p>

                        {req.elements?.length > 0 && (
                            <ul>
                                {req.elements.map((e, i) => (
                                    <li key={i}>{e}</li>
                                ))}
                            </ul>
                        )}
                        <FormControl
                            ref={formControlRef}
                            plaintext={readOnly}
                            className={`mt-2 ${readOnly && 'border-primary border-opacity-25'}`}
                            as={'textarea'}
                            rows={5}
                            name={'value'}
                            value={(inputValue as string) || ''}
                            onChange={(e) => {
                                handleValueChange(e.target.value);
                            }}
                            readOnly={readOnly || inputComplete}
                            minLength={minLength}
                            required
                            isValid={showValidation && valid}
                            isInvalid={showValidation && invalid}
                        />
                    </Form.Label>
                    {!readOnly && (
                        <div>
                            {minLength > 0 && inputValueString.length < minLength && (
                                <Form.Text className={'flex-grow-0'}>
                                    Enter at least {minLength} characters
                                </Form.Text>
                            )}
                            {maxLength && inputValueString.length > maxLength && (
                                <Form.Text className={'flex-grow-0 text-danger'}>
                                    Too many characters
                                </Form.Text>
                            )}
                            {maxLength && (
                                <Form.Text
                                    className={`flex-grow-0 float-end text-end ${(inputValueString.length || 0) > maxLength && 'text-danger'}`}
                                >
                                    {inputValueString.length || 0}/{maxLength}
                                </Form.Text>
                            )}
                        </div>
                    )}
                </FormGroup>
            )}
            {!readOnly && (
                <div className={'mt-1 w-25'}>
                    <Form.Label className={'d-flex'}>
                        <Form.Check
                            checked={inputComplete}
                            disabled={!valid && !inputComplete}
                            title={`Mark section as ${inputComplete ? 'in' : ''}complete`}
                            onChange={() => handleChangeComplete(!inputComplete)}
                            className={'me-2'}
                        />
                        Section is complete
                    </Form.Label>
                    <FormControl type={'hidden'} value={inputComplete ? 'true' : 'false'} />
                </div>
            )}
        </>
    );
};

export default EvaluationInput;
